import { memo } from 'react';

export type DesignPreviewWorkspaceIdentityProps = {
  /** When the share link has a custom logo, it renders INSTEAD of the workspace logo + name. */
  customLogoSrc?: string;
  workspaceLogoSrc?: string;
  workspaceName: string;
  /** Resolved foreground color for the workspace name (derived from the header background). */
  foregroundColor: string;
};

/**
 * The workspace identity shown in the header: workspace logo + name, OR — mirroring
 * PublicWorkspaceInfo — a custom logo that replaces the logo + name entirely.
 */
export const DesignPreviewWorkspaceIdentity = memo(
  ({ customLogoSrc, workspaceLogoSrc, workspaceName, foregroundColor }: DesignPreviewWorkspaceIdentityProps) => {
    if (customLogoSrc) {
      return (
        <div
          aria-hidden="true"
          className="size-5 shrink-0 rounded-4 bg-contain bg-left bg-no-repeat"
          data-testid="SHARE_DESIGN_TAB_PREVIEW_CUSTOM_LOGO"
          style={{ backgroundImage: `url(${customLogoSrc})` }}
        />
      );
    }

    return (
      <>
        {workspaceLogoSrc ? (
          <div
            aria-hidden="true"
            className="size-5 shrink-0 overflow-hidden rounded-4 border ads:border-ads-gray-5 border-gray-5 ads:bg-ads-gray-5 bg-center bg-cover bg-gray-5"
            data-testid="SHARE_DESIGN_TAB_PREVIEW_WORKSPACE_LOGO"
            style={{ backgroundImage: `url(${workspaceLogoSrc})` }}
          />
        ) : (
          <div
            aria-hidden="true"
            className="size-5 shrink-0 rounded-4 ads:bg-ads-gray-6 bg-gray-6"
            data-testid="SHARE_DESIGN_TAB_PREVIEW_WORKSPACE_LOGO_FALLBACK"
          />
        )}
        <span
          className="truncate font-semibold text-[9px] leading-[150%]"
          data-testid="SHARE_DESIGN_TAB_PREVIEW_WORKSPACE_NAME"
          style={{ color: foregroundColor }}
        >
          {workspaceName}
        </span>
      </>
    );
  },
);

DesignPreviewWorkspaceIdentity.displayName = 'DesignPreviewWorkspaceIdentity';
